import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0218 from "../lesson-18/lesson";
import lesson0318 from "../../course-03/lesson-18/lesson";
import lesson0502 from "../../course-05/lesson-02/lesson";
import lesson0503 from "../../course-05/lesson-03/lesson";
import lesson0504 from "../../course-05/lesson-04/lesson";

const linkLesson0219 = getLinkForCourseLesson(
  "4eb31243-b6cb-423f-919d-15bba103f7ce",
  "f95203e7-0946-4a0b-ba9e-28ecb546405c"
);
const linkLesson0218 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0218.id);
const linkLesson0318 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0318.id);
const linkLesson0502 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0502.id);
const linkLesson0503 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0503.id);
const linkLesson0504 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0504.id);
const linkLesson0505 = getLinkForCourseLesson(
  "53e2fab0-edf6-4634-a20e-ae0cdfe73cba",
  "398714f3-97ff-4937-845c-dcdb5e7d9bdd"
);

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>In <a href="${linkLesson0219}" target="_blank">Lesson 2.19</a>: Speech in reverberation and noise, the relationship between the modulation frequency of speech, the decay curve and reverberation is already explained. From this relationship, the parameter <em><span class=cmti-10>C</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub> was explained. This parameter is a measure of the clarity of speech in rooms. Several other parameters were developed in the last decades to assess the speech intelligibility of a signal or in a room. Speech intelligibility is inextricably linked with the characteristics of rooms, therefore it is useful to keep lessons <a href="${linkLesson0502}" target="_blank">5.2</a>, <a href="${linkLesson0503}" target="_blank">5.3</a>, <a href="${linkLesson0504}" target="_blank">5.4</a>, <a href="${linkLesson0505}" target="_blank">5.5</a> of the course on room and building acoustics at hand.
<h3 class=sectionHead>Clarity <em><span class=cmti-10>C</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub></h3>
<p>
   As explained in <a href="${linkLesson0219}" target="_blank">Lesson 2.19</a>: Speech in reverberation and noise, <em><span class=cmti-10>C</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub> is the ratio between the energies of the early and of the late sound expressed in&nbsp;dB. The early sound energy also contains the direct sound. A <em><span class=cmti-10>C</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub> value of 6&nbsp;dB usually results in good speech intelligibility. <a href="#" data-img-src=${config.ASSETS_URL}/02/21/theory/figure-1.svg data-img-alt="Figure 1. Changing ratio of early and late sound based on the absorption in a room [1].">Figure 1</a> shows the levels of two rooms with the same dimensions but with a different amount of sound absorption in the room. It shows how the relationship between early and late sound energy can be influenced by the distance from the source to the receiver (horizontal axis) and the amount of absorption in a room (left vs right graph). We see the relationships between the different types of sound energy (direct, direct+early, late). In the room with low absorption (left), the late sound (after 50ms) level is higher than the direct+early sound level close to the source. It is only closer to the source that the direct+early sound is higher than the late sound.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/02/21/theory/figure-1.svg alt="Figure 1. Changing ratio of early and late sound based on the absorption in a room [1]."><figcaption class=caption>Figure 1. Changing ratio of early and late sound based on the absorption in a room [1].</figcaption></figure>
<p><em><span class=cmti-10>C</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub> is negative (red) where the late sound is higher than the direct+early sound and it is positive (green) where the direct + early sound is higher than the late sound. <em><span class=cmti-10>C</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub> = 0dB where both intersect. Where the curve of the direct sound intersects the late sound curve, the direct sound energy is equal to the late sound. These borders can also be spatially mapped in a room if the position of the source and the characteristics of a room are known.
<p>
   <em><span class=cmti-10>C</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub> can be manually calculated by using the Sabine-Franklin-Jaeger theory of reverberant rooms. The mean free path
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=true>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   is calculated first (Eq. (1)). This is an approximation of the average distance a sound wave travels in a room. By subtracting Eq. (3) from Eq. (2), <em><span class=cmti-10>C</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub> can be deduced.The reverberation time can be calculated by using the Sabine formula explained in <a href="${linkLesson0505}" target="_blank">Lesson 5.5</a>: Reverberation time. This simplified calculation of <em><span class=cmti-10>C</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub> assumes a constant decay of sound. In reality, the sound can decay at different rates at different intervals depending on the source and the room geometry/material characteristics.
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mover accent=true>
               <mrow>
                  <mi>l</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-rel>=</mo>
            <mn>4</mn>
            <mfrac>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>where:
<p>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
   </math>
   : the volume of the room
<p>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>S</mi>
   </math>
   : the surface area of all the geometry in the room.
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>early</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>speech</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mo class=MathClass-bin>⋅</mo>
            <mi mathvariant=italic>log</mi>
            <mstyle mathsize=2.03em>
               <mrow>
                  <mo form=prefix fence=true>[</mo>
               </mrow>
            </mstyle>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mi>α</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>α</mi>
                  <mo class=MathClass-bin>⋅</mo>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <mstyle mathsize=2.03em>
               <mrow>
                  <mo form=prefix fence=true>(</mo>
               </mrow>
            </mstyle>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>exp</mi>
            <mstyle mathsize=1.19em>
               <mrow>
                  <mo form=prefix fence=true>(</mo>
               </mrow>
            </mstyle>
            <mfrac>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>0</mn>
                  <mo class=MathClass-punc>.</mo>
                  <mn>69</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>RT</mi>
               </mrow>
            </mfrac>
            <mstyle mathsize=1.19em>
               <mrow>
                  <mo form=prefix fence=true>)</mo>
               </mrow>
            </mstyle>
            <mstyle mathsize=2.03em>
               <mrow>
                  <mo form=prefix fence=true>)</mo>
               </mrow>
            </mstyle>
            <mstyle mathsize=2.03em>
               <mrow>
                  <mo form=prefix fence=true>]</mo>
               </mrow>
            </mstyle>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>dB</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>where:
<p>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>speech</mi>
         </mrow>
      </msub>
   </math>
   : the sound power of spoken speech in&nbsp;dB
<p>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>
   : distance from source to receiver
<p>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>α</mi>
   </math>
   : average sound absorption coefficient
<p>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>S</mi>
   </math>
   : surface area of the room
<p>
   ù
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=true>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   = mean free path
<p>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>RT</mi>
   </math>
   = reverberation time in the room
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>late</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>speech</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mo class=MathClass-bin>⋅</mo>
            <mi mathvariant=italic>log</mi>
            <mstyle mathsize=2.03em>
               <mrow>
                  <mo form=prefix fence=true>[</mo>
               </mrow>
            </mstyle>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mi>α</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>α</mi>
                  <mo class=MathClass-bin>⋅</mo>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <mi mathvariant=italic>exp</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>0</mn>
                  <mo class=MathClass-punc>.</mo>
                  <mn>69</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>RT</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
            <mstyle mathsize=2.03em>
               <mrow>
                  <mo form=prefix fence=true>]</mo>
               </mrow>
            </mstyle>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>dB</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(3)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>50</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>early</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>late</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>dB</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(4)
</table>
<h3 class=sectionHead>Definition <em><span class=cmti-10>D</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub></h3>
<p>
   The <em><span class=cmti-10>Definition</span></em> parameter
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mn>50</mn>
         </mrow>
      </msub>
   </math>
   is very similar to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mn>50</mn>
         </mrow>
      </msub>
   </math>
   , it also is a measure of the perceived clarity of sound. The “50” relates to 50ms decay time. Both parameters involve the relationship between the early sound (before 50ms) and the late sound (after 50ms). However,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mn>50</mn>
         </mrow>
      </msub>
   </math>
   describes to the ratio between the early energy and the total energy (early + late) expressed in percentage, where as
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mn>50</mn>
         </mrow>
      </msub>
   </math>
   is the ratio between the early and the late sound expressed in&nbsp;dB.
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mn>50</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi mathvariant=italic>early</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi mathvariant=italic>early</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi mathvariant=italic>late</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>[</mo>
               <mrow>
                  <mi>%</mi>
               </mrow>
               <mo form=postfix fence=true>]</mo>
            </mrow>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   Typical values for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mn>50</mn>
         </mrow>
      </msub>
   </math>
   range from 30% to 70%, with values of around 70% denoting excellent speech intelligibility. The parameter is listed in the ISO standard 3382-1 and represents a good predictor for speech intelligibility in rooms, whenever disturbing noise is not considered.
<h3 class=sectionHead>Useful-to-Detrimental index <em><span class=cmti-10>U</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub></h3>
<p>
   The parameter
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>50</mn>
         </mrow>
      </msub>
   </math>
   simply allows for the addition of noise into the equation similar to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mn>50</mn>
         </mrow>
      </msub>
   </math>
   . Noise is considered to be detrimental for speech intelligibility, so any noise can theoretically be equal to late sound. In Eq. (7) the noise is simply added to the late sound. The characteristics of the room, denoted by the product of the mean absorption
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>α</mi>
   </math>
   and the surface area
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>S</mi>
   </math>
   , are still important for noise as a change in absorption in the room changes the sound pressure level. Notice that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   is used on the right side of the Eq. (6).
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   refers to the sound power, not the sound pressure level (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   ). Using the sound power is more accurate as it is independent of the room characteristics.
<p>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Δ</mi>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>SN</mi>
         </mrow>
      </msub>
   </math>
   or the Speech-to-Noise ratio is calculated as the difference in sound power between the speech and the noise (6).
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>Δ</mi>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>SN</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>speech</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>noise</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>dB</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(6)
</table>
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
          <mfrac linethickness=0 numalign="right">
            <mrow>
                  <msub>
                  <mrow>
                    <mi>L</mi>
                  </mrow>
                  <mrow>
                    <mi>p</mi>
                    <mo class=MathClass-punc>,</mo>
                    <mi mathvariant=italic>late</mi>
                  </mrow>
              </msub>
              <mo class=MathClass-bin>+</mo>
              <msub>
                  <mrow>
                    <mi>L</mi>
                  </mrow>
                  <mrow>
                    <mi>p</mi>
                    <mo class=MathClass-punc>,</mo>
                    <mi mathvariant=italic>noise</mi>
                  </mrow>
              </msub>
              <mo class=MathClass-rel>=</mo>
            </mrow>
            <mrow>
            <msub>
                        <mrow>
                           <mi>L</mi>
                        </mrow>
                        <mrow>
                           <mi>w</mi>
                           <mo class=MathClass-punc>,</mo>
                           <mi mathvariant=italic>speech</mi>
                        </mrow>
                     </msub>
                     <mo class=MathClass-bin>+</mo>
                     <mn>10</mn>
                     <mo class=MathClass-bin>⋅</mo>
                     <mi mathvariant=italic>log</mi>
                     <mstyle mathsize=2.03em>
                        <mrow>
                           <mo form=prefix fence=true>[</mo>
                        </mrow>
                     </mstyle>
                     <mfrac>
                        <mrow>
                           <mn>4</mn>
                           <mo class=MathClass-open>(</mo>
                           <mn>1</mn>
                           <mo class=MathClass-bin>−</mo>
                           <mi>α</mi>
                           <mo class=MathClass-close>)</mo>
                        </mrow>
                        <mrow>
                           <mi mathvariant=italic>αS</mi>
                        </mrow>
                     </mfrac>
                     <mo class=MathClass-open>(</mo>
                     <mi mathvariant=italic>exp</mi>
                     <mo class=MathClass-open>(</mo>
                     <mfrac>
                        <mrow>
                           <mo class=MathClass-bin>−</mo>
                           <mn>0</mn>
                           <mo class=MathClass-punc>.</mo>
                           <mn>69</mn>
                        </mrow>
                        <mrow>
                           <mi mathvariant=italic>RT</mi>
                        </mrow>
                     </mfrac>
                     <mo class=MathClass-close>)</mo>
                     <mo class=MathClass-close>)</mo>
                     <mo class=MathClass-bin>+</mo>
                     <mfrac>
                        <mrow>
                           <mn>4</mn>
                           <mo class=MathClass-bin>∗</mo>
                           <mn>1</mn>
                           <msup>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                              <mrow>
                                 <mfrac>
                                    <mrow>
                                       <mo class=MathClass-bin>−</mo>
                                       <mi>Δ</mi>
                                       <msub>
                                          <mrow>
                                             <mi>L</mi>
                                          </mrow>
                                          <mrow>
                                             <mi mathvariant=italic>SN</mi>
                                          </mrow>
                                       </msub>
                                    </mrow>
                                    <mrow>
                                       <mn>10</mn>
                                    </mrow>
                                 </mfrac>
                              </mrow>
                           </msup>
                        </mrow>
                        <mrow>
                           <mi mathvariant=italic>αS</mi>
                        </mrow>
                     </mfrac>
                     <mstyle mathsize=2.03em>
                        <mrow>
                           <mo form=prefix fence=true>]</mo>
                        </mrow>
                     </mstyle>
                     <mo class=MathClass-open>(</mo>
                     <mi mathvariant=italic>dB</mi>
                     <mo class=MathClass-close>)</mo>
            </mrow>
          </mfrac>
         </math>
      <td class=eq-no>(7)
</table>
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>50</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>early</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mstyle mathsize=1.19em>
               <mrow>
                  <mo form=prefix fence=true>[</mo>
               </mrow>
            </mstyle>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>late</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>noise</mi>
               </mrow>
            </msub>
            <mstyle mathsize=1.19em>
               <mrow>
                  <mo form=prefix fence=true>]</mo>
               </mrow>
            </mstyle>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>dB</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   Finally,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>50</mn>
         </mrow>
      </msub>
   </math>
   (Eq. (8)) is obtained by subtracting the sum of the late sound and the noise (Eq. (7)) from the early sound (Eq. (2))
<h3 class=sectionHead>speech transmission index (STI)</h3>
<p>The STI was developed in the Netherlands starting in 1970 and has been improved since. Its use is widespread in acoustics because it consistently gives a good estimation of speech intelligibility under the influence of noise and reverberation. It can be used for assessing the intelligibility of speech through various communication channels, rooms are effectively considered channels. The STI values are simple to interpret but are based on a rather complex calculation. The basic mechanism of the STI parameter will be explained here.
<p>
   We already established that noise and reverberation have a detrimental effect on speech. This can be easily seen from the waveform of a sound file. <a href="#" data-img-src=${config.ASSETS_URL}/02/21/theory/figure-2.svg data-img-alt="Figure 2. Comparison of two waveforms, the waveform above is a dry anechoic recording, the waveform below is the same signal but convolved in a church in Rome (Sant Ignazio di Loyola).">Figure 2</a> shows the wavevorms of two speech signals that have the same origin. The waveform above is from an anechoic recording of speech. We can see that there are silences in between the words.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/02/21/theory/figure-2.svg alt="Figure 2. Comparison of two waveforms, the waveform above is a dry anechoic recording, the waveform below is the same signal but convolved in a church in Rome (Sant Ignazio di Loyola)."><figcaption class=caption>Figure 2. Comparison of two waveforms, the waveform above is a dry anechoic recording, the waveform below is the same signal but convolved in a church in Rome (Sant Ignazio di Loyola).</figcaption></figure>
<p>The waveform below is the anechoic recording above but convolved with the room impulse response of a church in Rome (at 10 meters from the source). Convolution, in this context, means applying the response of a room to a signal. More information about convolution can be found in <a href="${linkLesson0318}" target="_blank">Lesson 3.18</a>: FIR convolution, of the course on acoustical simulations and auralization. The reverberation of the church is very present in this recording. There are no silences in this recording, only a very slow decay is observed in the moments of silence (due to the long reverberation time). The peaks of the two waveforms coincide in time but their amplitude is different. This is because the (virtual) receiver is further away from the source.
<p>
   Speech is a modulated signal, and reverberation or noise (STI effectively does not make a distinction between both) affect the modulation depth of a signal. A schematic representation of modulation depth is shown in <a href="#" data-img-src=${config.ASSETS_URL}/02/21/theory/figure-3.svg data-img-alt="Figure 3. Comparison of the modulation depth of an anechoic speech recording (m1) and a convolved recording (m2).">Figure 3</a>. The anechoic recording of <a href="#" data-img-src=${config.ASSETS_URL}/02/21/theory/figure-2.svg data-img-alt="Figure 2. Comparison of two waveforms, the waveform above is a dry anechoic recording, the waveform below is the same signal but convolved in a church in Rome (Sant Ignazio di Loyola).">Figure 2</a> is placed in the top half of the waveform, the convolved waveform is placed on the lower half. It can be observed how the signals resemble each other to some extent. The peaks in the top half of the waveform coincide with the peaks in bottom half. Furthermore, it can be seen that the modulation depth up to t= 0.9 s (the difference between the highest level and the lowest level) is significantly reduced (m1 vs. m2).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/02/21/theory/figure-3.svg alt="Figure 3. Comparison of the modulation depth of an anechoic speech recording (m1) and a convolved recording (m2)."><figcaption class=caption>Figure 3. Comparison of the modulation depth of an anechoic speech recording (m1) and a convolved recording (m2).</figcaption></figure>
<p>It turns out that this amplitude modulation depth correlates well with speech intelligibility. A smaller modulation depth means lower speech intelligibility in most cases. This has been extensively tested through phonetically balanced word comprehension tests by the original developers of the STI parameter.
<p>In <a href="${linkLesson0218}" target="_blank">Lesson 2.18</a>: Speech production, it was explained that consonants carry less acoustic energy than vowels. Their amplitude, and modulation depth are lower, which is why the addition of noise or reverberation affects the intelligibility of consonants more significantly.
<p>As explained in <a href="${linkLesson0502}" target="_blank">Lesson 5.2</a>: How a room responds to sound, and <a href="${linkLesson0505}" target="_blank">Lesson 5.5</a>: Reverberation time, the decay of sound energy in a room depends mainly on room volume, mean absorption, source-receiver distance, the shape of a space. The decay can be faster or slower based on these factors. This is why faster modulations in speech are more affected by reverberation than slower modulations. Speaking slowly can help to improve the intelligibility of one’s speech significantly, this is not always practical and comfortable. Pausing in between words and syllables is equal to waiting until the modulation depth is larger. Speaking slowly does not help in the case of noise as the modulation depth does not increase with time (with a steady-state noise signal).
<p>This effect can be quantitatively demonstrated with Eq. (9). The result is the 3&nbsp;dB attenuation threshold. With a reverberation time of 0.5 s all modulation frequencies above 4.4&nbsp;Hz are attenuated with at least 3&nbsp;dB. All modulation frequencies that are attenuated less than 3&nbsp;dB are unaffected when it comes to speech intelligibility.
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>3</mn>
                  <mi mathvariant=italic>dB</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>13</mn>
                  <mo class=MathClass-punc>.</mo>
                  <mn>8</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>πT</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>T</mi>
   </math>
   being reverberation time in the room.
<p>
<a href="#" data-img-src=${config.ASSETS_URL}/02/21/theory/figure-4.svg data-img-alt="Figure 4. the effect of reverberation time on the speech modulation spectrum (Lau Nijs, bk.nijsnet.com).">Figure 4</a> shows the higher impact of reverberation on the higher modulation frequencies of speech. The dots on the lines show the point where the reduction of the amplitude is 3&nbsp;dB. Eq. (9) and <a href="#" data-img-src=${config.ASSETS_URL}/02/21/theory/figure-4.svg data-img-alt="Figure 4. the effect of reverberation time on the speech modulation spectrum (Lau Nijs, bk.nijsnet.com).">Figure 4</a> are valid with a consistent exponential decay of sound in a room.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/02/21/theory/figure-4.svg alt="Figure 4. the effect of reverberation time on the speech modulation spectrum (Lau Nijs, bk.nijsnet.com)."><figcaption class=caption>Figure 4. the effect of reverberation time on the speech modulation spectrum (Lau Nijs, bk.nijsnet.com).</figcaption></figure>
<h4 class=subsectionHead>Test signal</h4>
<p>The STI parameter calculation does not take speech as a test signal. That would not be practical. STI can be measured in situ or predicted in geometrical room acoustic software based on a standard test signal. The test signal along with the convoluted signal at a frequency of 500&nbsp;Hz and a modulation frequency of 10&nbsp;Hz is shown in <a href="#" data-img-src=${config.ASSETS_URL}/02/21/theory/figure-5.svg data-img-alt="Figure 5. The normalised amplitude of two signals, in red: the original test signal, in blue: the signal as received through the communication channel/room. Frequency: 500Hz, modulation frequency 10Hz.">Figure 5</a>.
<p>Based on the value of m (modulation transfer function) and m-1 that is measured (shown in <a href="#" data-img-src=${config.ASSETS_URL}/02/21/theory/figure-5.svg data-img-alt="Figure 5. The normalised amplitude of two signals, in red: the original test signal, in blue: the signal as received through the communication channel/room. Frequency: 500Hz, modulation frequency 10Hz.">Figure 5</a>) the speech-to-noise ratio (SNR) is calculated. This does not refer to the “Signal-to-noise ratio” but it does resemble it.
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi mathvariant=italic>SNR</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi>m</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(10)
</table>
<figure class=figure>
   <p>
      <img src=${config.ASSETS_URL}/02/21/theory/figure-5.svg alt="Figure 5. The normalised amplitude of two signals, in red: the original test signal, in blue: the signal as received through the communication channel/room. Frequency: 500Hz, modulation frequency 10Hz.">
      <figcaption class=caption>Figure 5. The normalised amplitude of two signals, in red: the original test signal, in blue: the signal as received through the communication channel/room. Frequency: 500Hz, modulation frequency 10Hz.</figcaption>
</figure>
<p>In practice a SNR value of +15&nbsp;dB means that reverberation or noise is virtually unnoticeable. Inversely an SNR value of -15&nbsp;dB practically means that speech is completely unintelligible. This relationship can be mathematically expressed in a simplified way by Eq. (11).
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi mathvariant=italic>TI</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>SNR</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mn>15</mn>
               </mrow>
               <mrow>
                  <mn>30</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(11)
</table>
<p>This transmission index (TI) value is calculated for every combination of modulation frequency in one third octave bands (0.3-20&nbsp;Hz) and octave band between (125-8000&nbsp;Hz). The TI values are then averaged over the different modulation frequency, one third octave bands. They are also averaged over octave bands but with a weighting factor (<a href="#" data-img-src=${config.ASSETS_URL}/02/21/theory/table-1.svg data-img-alt="Table 1. Weighting factors per octave band for the STI frequency.">Table 1</a>). This method is based on many listening tests involving phonetically balanced (PB) word score tests. The octave bands 2000&nbsp;Hz and 4000&nbsp;Hz are given more weight in the averaging because these frequencies contain the consonants of speech that carry less acoustic energy and are important for speech intelligibility.
<figure class=float>
  <figcaption>Table 1. Weighting factors per octave band for the STI frequency.</figcaption>
  <img src=${config.ASSETS_URL}/02/21/theory/table-1.svg alt="Table 1. Weighting factors per octave band for the STI frequency.">
</figure>
<p>Finally, one single value is obtained. More tests established an average subjective evaluation of the STI value. <a href="#" data-img-src=${config.ASSETS_URL}/02/21/theory/table-2.svg data-img-alt="Table 2. Subjective evaluation of STI value ranges.">Table 2</a> shows this evaluation according to the STI value.
<figure class=float>
  <figcaption>Table 2. Subjective evaluation of STI value ranges.</figcaption>
  <img src=${config.ASSETS_URL}/02/21/theory/table-2.svg alt="Table 2. Subjective evaluation of STI value ranges.">
</figure>
<h3>Glossary</h3>
<dl>
  <dt>PB
  <dd>phonetically balanced

  <dt>SNR
  <dd>speech-to-noise ratio

  <dt>STI
  <dd>speech transmission index

  <dt>TI
  <dd>transmission index
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-Nijs_2010a>[1]
   <dd class=thebibliography id=bib-1>
      <p>
         Nijs, L. (2010a). Akoestische maten van spraakverstaanbaarheid. <span class=cmti-10>Bk.Nijsnet</span>.
         <a class=url href=https://bk.nijsnet.com/02220_Indices_Spraakverstaan.aspx#_ftn4 target="_blank">
            https://bk.nijsnet.com/02220_Indices_Spraakverstaan.aspx#_ftn4
         </a>
   <dt class=thebibliography id=X0-Nijs_2010b>[2]
   <dd class=thebibliography id=bib-2>
      <p>Nijs, L. (2010b). Theorie u50.
   <dt class=thebibliography id=X0-Houtgast_Steeneken_1971>[3]
   <dd class=thebibliography id=bib-3>
      <p>Houtgast, T., & Steeneken, H. (1971). Evaluation of speech transmission channels by using artificial signals. <span class=cmti-10>Acustica</span>, <span class=cmti-10>25</span>, 355–367.
   <dt class=thebibliography id=X0-Houtgast_Steeneken_1973>[4]
   <dd class=thebibliography id=bib-4>
      <p>Houtgast, T., & Steeneken, H. (1973). The modulation transfer function in room acoustics as a predictor of speech intelligibility. <span class=cmti-10>Acustica</span>, <span class=cmti-10>28</span>, 66–73.
   <dt class=thebibliography id=X0-Houtgast_et_al_2002>[5]
   <dd class=thebibliography id=bib-5>
      <p>Houtgast, T., Steeneken, H., Ahnert, W., Braida, L., Drullman, R., Festen, J. et al. (2002). Past,present,and future of the speech transmission index.
</dl>`;

export default theory;
